import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '../../utils/toast';
import { ArrowRightIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { CreatePostSection } from '../../components/dashboard/createPostSection';
import { CurrentEventsCarousel } from '../../components/dashboard/currentEventsCarousel';
import { EmptyState } from '../../components/dashboard/emptyState';
import { EventsPanelSkeleton } from '../../components/dashboard/skeleton';
import { EventStatsCards } from '../../components/dashboard/eventStatsCards';
import { InsightsCard } from '../../components/dashboard/insightsCard';
import { MoneyFlowChart } from '../../components/dashboard/moneyFlowChart';
import { TotalFundsCard } from '../../components/dashboard/totalFundsCard';
import { ManagerRequestPopup } from '../../components/dashboard/managerRequestPopUp';
import { MarketplaceCards } from '../../components/dashboard/marketplaceCards';
import { ProfileStatsSection } from '../../components/dashboard/profileStats';
import { PendingActionBanner } from '../../components/dashboard/pendingActionBanner';
import {
  fetchProfile,
  getActiveDeals,
  getAllEvents,
  getClosedDealPayments,
  getDashboardPendingActions,
  getFighterDashboardStats,
  getFighterManagerLinks,
  getProfile,
  getProfileViewStats,
  updateFighterManagerLinkStatus,
} from '../../services/api';
import {
  DashboardPendingActionDto,
  DealResponseDto,
  EventListResponse,
  FighterDashboardStatsDto,
  FighterManagerLinkResponseDto,
  PaymentResponseDto,
  Profile,
  TrackingResponseDto,
} from '../../types/types';
import { getEventAvatarColor, getEventInitials } from '../../utils/common/formatUtils';
import MoneyProjection from '../events/moneyProjection';
import {
  DealsSection,
  DealsSummaryCards,
  DealsListCard,
  DealTab,
} from '../../components/dashboard/dealsSection';

export const DashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation('dashboard');
  const loginResponse = JSON.parse(localStorage.getItem('loginResponse') || '{}');
  const username = loginResponse.username || 'exampleUser';
  const loggedInUserName = loginResponse.username;
  const loggedInUserProfileType = loginResponse.profileType;

  const [fighterStats, setFighterStats] = useState<FighterDashboardStatsDto | null>(null);

  const [pendingActions, setPendingActions] = useState<DashboardPendingActionDto[]>([]);

  const [links, setLinks] = useState<FighterManagerLinkResponseDto[]>([]);
  const [userProfile, setUserProfile] = useState<Profile | null>(null);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const [profileMessage, setProfileMessage] = useState<string>(t('loading'));
  const [stats, setStats] = useState<TrackingResponseDto | null>(null);
  const [displayedProfileViews, setDisplayedProfileViews] = useState<number>(0);
  const [profileRange, setProfileRange] = useState<'7days' | 'month' | 'all'>('month');

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [payments, setPayments] = useState<PaymentResponseDto[]>([]);

  const [events, setEvents] = useState<EventListResponse[]>([]);
  const [eventsLoading, setEventsLoading] = useState<boolean>(true);
  const [statsLoading, setStatsLoading] = useState<boolean>(true);
  const [selectedEventId, setSelectedEventId] = useState<string>('');

  const [eventSearch, setEventSearch] = useState<string>('');
  const [isEventSelectorOpen, setIsEventSelectorOpen] = useState(false);

  const selectedEvent = events.find((event) => event.id === selectedEventId);

  const [dealTab, setDealTab] = useState<DealTab>('inactive');
  const [activeDeals, setActiveDeals] = useState<DealResponseDto[]>([]);
  const [activeDealsLoading, setActiveDealsLoading] = useState<boolean>(false);

  const isManager =
    loginResponse?.profileType === 'MANAGER' ||
    loginResponse?.profileType === 'ORGANIZATION';
  const isFighter = loginResponse?.profileType === 'FIGHTER';

  useEffect(() => {
    const savedLanguage = localStorage.getItem('i18nextLng') || 'en';
    if (i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  useEffect(() => {
    const fetchEvents = async (): Promise<void> => {
      if (!loggedInUserName) {
        setEventsLoading(false);
        return;
      }

      try {
        const eventList = await getAllEvents(loggedInUserName);
        setEvents(eventList);
      } catch (err) {
        toast.error('Failed to load events');
      } finally {
        setEventsLoading(false);
      }
    };

    fetchEvents();
  }, [loggedInUserName]);

  useEffect(() => {
    if (events.length === 0) {
      setSelectedEventId('');
      return;
    }

    if (!events.some((event) => event.id === selectedEventId)) {
      setSelectedEventId(events[0].id);
    }
  }, [events, selectedEventId]);

  useEffect(() => {
    if (!loggedInUserName) return;

    let isCancelled = false;
    let counterInterval: ReturnType<typeof setInterval> | undefined;

    const fetchProfileStats = async () => {
      setStatsLoading(true);
      try {
        const statResult = await getProfileViewStats(loggedInUserName, profileRange);
        if (isCancelled) return;

        setStats({
          ...statResult,
          countryViews: statResult.countryViews || [],
        });

        let count = 0;
        const target = statResult.totalViews || 0;
        const step = Math.ceil(target / 30);

        counterInterval = setInterval(() => {
          count += step;
          if (count >= target) {
            count = target;
            clearInterval(counterInterval);
          }
          setDisplayedProfileViews(count);
        }, 20);
      } catch (err) {
        if (!isCancelled) setError(t('failed_to_load_profile_view_stats'));
      } finally {
        if (!isCancelled) setStatsLoading(false);
      }
    };

    fetchProfileStats();

    return () => {
      isCancelled = true;
      if (counterInterval) clearInterval(counterInterval);
    };
  }, [profileRange, loggedInUserName, t]);

  useEffect(() => {
    const fetchData = async () => {
      if (!loggedInUserName) {
        setError(t('no_user_logged_in'));
        setProfileMessage(t('no_user_logged_in'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const profileResponse = await fetchProfile();
        setUserProfile(profileResponse);

        try {
          const message = await getProfile();
          setProfileMessage(message);
        } catch (err) {
          setProfileMessage(t('error_loading_profile'));
        }

        if (profileResponse?.profileType === 'FIGHTER') {
          try {
            const linksResponse = await getFighterManagerLinks(username);
            if (Array.isArray(linksResponse)) {
              const pendingLinks = linksResponse.filter(
                (link) => !link.active && !link.removedAt
              );
              setLinks(pendingLinks);
              if (pendingLinks.length > 0) {
                setShowPopup(true);
              }
            } else {
              console.error('API response is not an array:', linksResponse);
              setLinks([]);
            }
          } catch (error) {
            console.error('Error fetching fighter-manager links:', error);
            setLinks([]);
          }
        }

        if (!isManager) {
          const paymentsResponse = await getClosedDealPayments();
          setPayments(paymentsResponse.paymentResponses || []);
        }

        if (isFighter) {
          try {
            setFighterStats(await getFighterDashboardStats());
          } catch (err) {
            toast.error(t('failed_to_load_fighter_stats'));
          }
        }

        try {
          const pendingResponse = await getDashboardPendingActions();
          setPendingActions(pendingResponse.actions);
        } catch (err) {
          setPendingActions([]);
        }
      } catch (error) {
        setError(t('failed_to_load_dashboard_data'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [loggedInUserName, loggedInUserProfileType, username, t]);

  const handleDealTabChange = async (tab: DealTab) => {
    setDealTab(tab);
    if (tab === 'active' && activeDeals.length === 0) {
      try {
        setActiveDealsLoading(true);
        const response = await getActiveDeals();
        const deals = Array.isArray(response)
          ? response
          : response?.deals ?? response?.content ?? [];
        setActiveDeals(deals);
      } catch (err) {
        toast.error('Failed to load active deals');
      } finally {
        setActiveDealsLoading(false);
      }
    }
  };

  const handleLinkAction = async (linkId: number, approved: boolean) => {
    try {
      setActionLoading(linkId);
      await updateFighterManagerLinkStatus(username, linkId, approved);
      setLinks(links.filter((link) => link.id !== linkId));
      if (links.length === 1) {
        setShowPopup(false);
      }
      toast.success(approved ? t('accepted') : t('declined'));
    } catch (error) {
      console.error(`Error ${approved ? 'accepting' : 'declining'} link:`, error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const handleEventRowClick = (eventId: string) => {
    setSelectedEventId(eventId);
  };

  const getEventLocation = (event: EventListResponse): string => event.venue || event.city || '';

  const filteredEvents = events.filter((event) => {
    const query = eventSearch.trim().toLowerCase();
    if (query === '') return true;

    return (
      String(event.name || '').toLowerCase().includes(query) ||
      getEventLocation(event).toLowerCase().includes(query)
    );
  });

  if (loading && !isManager) {
    return (
      <div className="min-h-screen bg-slate-50 p-4 lg:p-6 font-sans">
        <div className="max-w-full mx-auto">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-start animate-pulse">
            <div className="min-w-0 flex-1 space-y-4">
              <div className="rounded-3xl bg-white shadow-sm p-5 flex gap-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex-1 space-y-2">
                    <div className="h-3 bg-slate-200 rounded-full w-2/3" />
                    <div className="h-6 bg-slate-200 rounded-full w-1/2" />
                    <div className="h-2 bg-slate-100 rounded-full w-3/4" />
                  </div>
                ))}
              </div>

              <div className="rounded-3xl bg-white shadow-sm p-5 space-y-3">
                <div className="h-4 bg-slate-200 rounded-full w-1/4" />
                <div className="h-px bg-slate-100 rounded w-full" />
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-4 py-2">
                    <div className="h-8 w-8 rounded-2xl bg-slate-200 shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 bg-slate-200 rounded-full w-3/4" />
                      <div className="h-2 bg-slate-100 rounded-full w-1/2" />
                    </div>
                    <div className="h-3 bg-slate-200 rounded-full w-16" />
                    <div className="h-3 bg-slate-200 rounded-full w-16" />
                    <div className="h-3 bg-slate-200 rounded-full w-16" />
                  </div>
                ))}
              </div>
            </div>

            <aside className="flex w-full flex-col gap-4 xl:w-96 xl:shrink-0">
              <div className="rounded-3xl bg-white shadow-sm p-4 space-y-3">
                <div className="h-3 bg-slate-200 rounded-full w-1/3" />
                <div className="h-2 bg-slate-100 rounded-full w-1/2" />
                <div className="flex gap-3 items-center">
                  <div className="h-10 w-10 rounded-3xl bg-slate-200 shrink-0" />
                  <div className="flex-1 h-10 rounded-xl bg-slate-100" />
                </div>
                <div className="h-16 rounded-xl bg-slate-100" />
              </div>

              <div className="rounded-3xl bg-white shadow-sm p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <div className="h-3 bg-slate-200 rounded-full w-1/3" />
                  <div className="h-6 bg-slate-200 rounded-full w-24" />
                </div>
                <div className="h-32 bg-slate-100 rounded-2xl" />
                <div className="grid grid-cols-2 gap-2">
                  <div className="h-12 bg-slate-100 rounded-2xl" />
                  <div className="h-12 bg-slate-100 rounded-2xl" />
                </div>
              </div>

              <div className="rounded-3xl bg-white shadow-sm p-4 space-y-3">
                <div className="h-3 bg-slate-200 rounded-full w-1/3" />
                <div className="flex gap-3">
                  <div className="flex-1 h-40 bg-slate-100 rounded-2xl" />
                  <div className="flex-1 h-40 bg-slate-100 rounded-2xl" />
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <ManagerRequestPopup
        showPopup={showPopup}
        links={links}
        userProfile={userProfile}
        actionLoading={actionLoading}
        onLinkAction={handleLinkAction}
        onClosePopup={handleClosePopup}
      />

      {/* Desktop page title */}
      <div className="hidden lg:flex items-center bg-white border-b border-slate-200/80 px-6 py-4">
        <h1 className="text-xl font-bold text-gray-900 font-['Plus_Jakarta_Sans']">{t('dashboard_title')}</h1>
      </div>

      <div className="px-4 pt-4 pb-6 lg:p-6 font-sans">
        <div className="max-w-full mx-auto">
          {pendingActions.length > 0 && (
            <div className="mb-6 flex flex-col gap-3">
              {pendingActions.map((action) => (
                <PendingActionBanner key={action.type} action={action} />
              ))}
            </div>
          )}

          {isManager && (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-4">
              <div className="xl:col-span-2">
                <TotalFundsCard />
              </div>
              <div className="xl:col-span-2">
                <EventStatsCards />
              </div>

              <div className="min-w-0 xl:col-span-3">
                {eventsLoading ? (
                  <EventsPanelSkeleton />
                ) : events.length > 0 ? (
                  <div className="h-full rounded-3xl border border-slate-200/80 bg-white p-4 sm:p-6 shadow-[0px_0px_1px_0px_rgba(0,0,0,0.06),0px_1px_2px_0px_rgba(0,0,0,0.06),0px_2px_4px_0px_rgba(0,0,0,0.04)] font-['Plus_Jakarta_Sans']">
                    <div className="grid grid-cols-12 gap-6 lg:gap-8">
                      <div className="hidden md:flex col-span-12 md:col-span-4 lg:w-60 lg:col-span-3 flex-col gap-4">
                        <div className="flex flex-col justify-start items-start">
                          <div className="text-foreground-foreground text-sm font-medium font-['Plus_Jakarta_Sans'] leading-5">
                            {t('events_title', 'Events')}
                          </div>
                        </div>

                        <div>
                          <input
                            type="text"
                            value={eventSearch}
                            onChange={(e) => setEventSearch(e.target.value)}
                            placeholder={t('search_events', 'Search events...')}
                            className="block h-9 w-full rounded-2xl border border-slate-200 bg-slate-50/80 px-3 text-xs text-slate-700 outline-none placeholder-slate-400 focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-50 transition-all font-['Plus_Jakarta_Sans']"
                          />
                        </div>

                        <div className="max-h-[520px] overflow-y-auto space-y-1 pr-1">
                          {filteredEvents.length === 0 ? (
                            <div className="py-8 text-center text-xs text-slate-400 font-['Plus_Jakarta_Sans']">
                              {t('no_events_found', 'No events found')}
                            </div>
                          ) : (
                            filteredEvents.map((event) => {
                              const isSelected = selectedEventId === event.id;
                              const description =
                                getEventLocation(event);
                              const eventInitials = getEventInitials(event.name);
                              const avatarColor = getEventAvatarColor(event.name || '');

                              return (
                                <div
                                  key={event.id}
                                  onClick={() => handleEventRowClick(event.id)}
                                  className={`self-stretch min-h-9 px-3 py-1.5 rounded-[20px] inline-flex justify-start items-center gap-3 cursor-pointer transition-all ${
                                    isSelected
                                      ? 'bg-blue-100 text-slate-900 font-medium'
                                      : 'hover:bg-slate-50 text-slate-700'
                                  }`}
                                >
                                  <div
                                    className="size-9 shrink-0 rounded-2xl flex items-center justify-center"
                                    style={{ backgroundColor: avatarColor.bg }}
                                  >
                                    <span
                                      className="text-xs font-bold leading-none select-none tracking-wide"
                                      style={{ color: avatarColor.text }}
                                    >
                                      {eventInitials}
                                    </span>
                                  </div>
                                  <div className="flex-1 inline-flex flex-col justify-start items-start min-w-0">
                                    <span className="self-stretch justify-start truncate text-slate-900 text-sm font-medium font-['Plus_Jakarta_Sans'] leading-5">
                                      {event.name}
                                    </span>
                                    {description && (
                                      <span className="self-stretch justify-start truncate text-slate-400 text-xs font-normal font-['Plus_Jakarta_Sans'] leading-4">
                                        {description}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>

                      <div className="md:hidden col-span-12 flex flex-col gap-2">
                        <div className="text-foreground-foreground text-sm font-medium font-['Plus_Jakarta_Sans'] leading-5">
                          {t('events_title', 'Events')}
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsEventSelectorOpen(true)}
                          className="w-full flex items-center justify-between p-3 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors shadow-sm text-left"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {selectedEvent ? (
                              <>
                                <div
                                  className="size-9 shrink-0 rounded-2xl flex items-center justify-center text-xs font-bold"
                                  style={{
                                    backgroundColor: getEventAvatarColor(selectedEvent.name || '').bg,
                                    color: getEventAvatarColor(selectedEvent.name || '').text,
                                  }}
                                >
                                  {getEventInitials(selectedEvent.name)}
                                </div>
                                <div className="min-w-0 flex flex-col">
                                  <span className="text-sm font-semibold text-slate-900 truncate">
                                    {selectedEvent.name}
                                  </span>
                                  {getEventLocation(selectedEvent) && (
                                    <span className="text-xs text-slate-400 truncate">
                                      {getEventLocation(selectedEvent)}
                                    </span>
                                  )}
                                </div>
                              </>
                            ) : (
                              <span className="text-sm text-slate-500">{t('choose_event', 'Select an event...')}</span>
                            )}
                          </div>
                          <ChevronDownIcon className="w-5 h-5 text-slate-400 shrink-0 ml-2" />
                        </button>
                      </div>

                      {isEventSelectorOpen && (
                        <div className="fixed inset-0 z-50 flex items-end justify-center md:hidden">
                          <div
                            className="fixed inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity"
                            onClick={() => setIsEventSelectorOpen(false)}
                          />
                          <div className="relative w-full max-h-[80vh] overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl flex flex-col gap-3 z-10">
                            <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                              <h2 className="text-base font-bold text-gray-900">
                                {t('select_event', 'Select Event')}
                              </h2>
                              <button
                                type="button"
                                onClick={() => setIsEventSelectorOpen(false)}
                                className="size-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200"
                                aria-label="Close"
                              >
                                <XMarkIcon className="w-5 h-5" />
                              </button>
                            </div>

                            <div>
                              <input
                                type="text"
                                value={eventSearch}
                                onChange={(e) => setEventSearch(e.target.value)}
                                placeholder={t('search_events', 'Search events...')}
                                className="block h-9 w-full rounded-2xl border border-slate-200 bg-slate-50/80 px-3 text-xs text-slate-700 outline-none placeholder-slate-400 focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-50 transition-all font-['Plus_Jakarta_Sans']"
                              />
                            </div>

                            <div className="max-h-[50vh] overflow-y-auto space-y-1">
                              {filteredEvents.length === 0 ? (
                                <div className="py-8 text-center text-xs text-slate-400 font-['Plus_Jakarta_Sans']">
                                  {t('no_events_found', 'No events found')}
                                </div>
                              ) : (
                                filteredEvents.map((event) => {
                                  const isSelected = selectedEventId === event.id;
                                  const description =
                                    getEventLocation(event);
                                  const eventInitials = getEventInitials(event.name);
                                  const avatarColor = getEventAvatarColor(event.name || '');

                                  return (
                                    <div
                                      key={event.id}
                                      onClick={() => {
                                        handleEventRowClick(event.id);
                                        setIsEventSelectorOpen(false);
                                      }}
                                      className={`w-full min-h-11 px-3 py-2 rounded-2xl flex items-center gap-3 cursor-pointer transition-all ${
                                        isSelected
                                          ? 'bg-blue-50 text-slate-900 font-medium border border-blue-200'
                                          : 'hover:bg-slate-50 text-slate-700'
                                      }`}
                                    >
                                      <div
                                        className="size-9 shrink-0 rounded-2xl flex items-center justify-center"
                                        style={{ backgroundColor: avatarColor.bg }}
                                      >
                                        <span
                                          className="text-xs font-bold leading-none select-none tracking-wide"
                                          style={{ color: avatarColor.text }}
                                        >
                                          {eventInitials}
                                        </span>
                                      </div>
                                      <div className="flex-1 flex flex-col justify-start items-start min-w-0 text-left">
                                        <span className="w-full truncate text-slate-900 text-sm font-medium font-['Plus_Jakarta_Sans'] leading-5">
                                          {event.name}
                                        </span>
                                        {description && (
                                          <span className="w-full truncate text-slate-400 text-xs font-normal font-['Plus_Jakarta_Sans'] leading-4">
                                            {description}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="col-span-12 md:col-span-8 lg:flex-1 lg:col-span-9 min-w-0">
                        {selectedEvent ? (
                          <MoneyProjection
                            eventId={selectedEvent.id}
                            eventName={selectedEvent.name}
                            matchupCount={selectedEvent.matchupCount}
                          />
                        ) : (
                          <div className="flex min-h-[260px] items-center justify-center px-4 py-10 text-center font-['Plus_Jakarta_Sans']">
                            <div>
                              <p className="text-sm font-semibold text-slate-700">{t('select_event')}</p>
                              <p className="mt-1 text-xs text-slate-400">{t('choose_event_left')}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-full rounded-3xl bg-white p-4 shadow-sm ring-1 ring-black/5 font-['Plus_Jakarta_Sans']">
                    <p className="text-sm font-medium leading-5 text-gray-900">{t('events_title')}</p>
                    <EmptyState text={t('no_data_to_display')} />
                  </div>
                )}
              </div>
              <div className="xl:col-span-1">
                <InsightsCard />
              </div>

              <div className="min-w-0 xl:col-span-3">
                <CurrentEventsCarousel />
              </div>
              <div className="xl:col-span-1">
                <ProfileStatsSection
                  stats={stats}
                  displayedProfileViews={displayedProfileViews}
                  profileRange={profileRange}
                  isLoading={statsLoading}
                  onProfileRangeChange={setProfileRange}
                />
              </div>
            </div>
          )}

          {!isManager && (
            <div className="flex flex-col gap-6 xl:grid xl:grid-cols-[minmax(0,3fr)_minmax(0,1fr)] xl:items-start">
              <div className="contents xl:flex xl:min-w-0 xl:flex-col xl:space-y-6">
                <div className="order-2 w-full xl:order-1">
                  <DealsSummaryCards stats={fighterStats} />
                </div>

                 {isFighter && (
                  <div className="order-4 w-full xl:order-2">
                    <MoneyFlowChart />
                  </div>
                )}

                <div className="order-5 w-full xl:order-2 xl:flex xl:flex-col">
                  <DealsListCard
                    isFighter={isFighter}
                    isManager={isManager}
                    payments={payments}
                    activeDeals={activeDeals}
                    activeDealsLoading={activeDealsLoading}
                    dealTab={dealTab}
                    onDealTabChange={handleDealTabChange}
                  />
                </div>
              </div>

              <aside className="contents xl:flex xl:min-w-0 xl:flex-col xl:gap-6">
                <div className="order-1 w-full xl:order-none">
                  <CreatePostSection />
                </div>

                <div className="order-3 w-full xl:order-none">
                  <ProfileStatsSection
                    stats={stats}
                    displayedProfileViews={displayedProfileViews}
                    profileRange={profileRange}
                    isLoading={statsLoading}
                    onProfileRangeChange={setProfileRange}
                  />
                </div>

                <div className="order-6 w-full xl:order-none">
                  <MarketplaceCards
                    username={loggedInUserName}
                    profileImage={userProfile?.profileImage}
                    fullName={`${userProfile?.firstName ?? ''} ${userProfile?.lastName ?? ''}`.trim()}
                  />
                </div>
              </aside>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};